/* eslint-disable perfectionist/sort-objects -- Keep translation locale columns in APP_LOCALES order. */
import { CATALOG_LOCALES, CatalogLocale } from '../locale';
import { defineDomainTranslations } from './defineDomainTranslations';

type TranslationRow = Record<CatalogLocale, string>;

const TRIAL_PAYWALL_TRANSLATION_ROWS = {
  'trialPaywall.defaultTitle': {
    en: 'Unlock all download options',
    es: 'Desbloquea todas las opciones de descarga',
    pt: 'Desbloqueie todas as opções de download',
    fr: 'Débloquez toutes les options de téléchargement',
    it: 'Sblocca tutte le opzioni di download',
    ru: 'Откройте все варианты скачивания',
  },
  'trialPaywall.includes.enterprise': {
    en: 'The Enterprise Plan Includes:',
    es: 'El plan Enterprise incluye:',
    pt: 'O plano Enterprise inclui:',
    fr: 'Le forfait Enterprise inclut :',
    it: 'Il piano Enterprise include:',
    ru: 'Тариф Enterprise включает:',
  },
  'trialPaywall.includes.annual': {
    en: 'All Annual Plans Include:',
    es: 'Todos los planes anuales incluyen:',
    pt: 'Todos os planos anuais incluem:',
    fr: 'Tous les forfaits annuels incluent :',
    it: 'Tutti i piani annuali includono:',
    ru: 'Все годовые тарифы включают:',
  },
  'trialPaywall.includes.all': {
    en: 'All Plans Include:',
    es: 'Todos los planes incluyen:',
    pt: 'Todos os planos incluem:',
    fr: 'Tous les forfaits incluent :',
    it: 'Tutti i piani includono:',
    ru: 'Все тарифы включают:',
  },
  'trialPaywall.benefits.heading': {
    en: 'With {plan} You Get:',
    es: 'Con {plan} obtienes:',
    pt: 'Com o {plan}, você recebe:',
    fr: 'Avec {plan}, vous bénéficiez de :',
    it: 'Con {plan} ottieni:',
    ru: 'С тарифом {plan} вы получаете:',
  },
  'trialPaywall.period.payAnnual': {
    en: 'Pay annually',
    es: 'Pagar anualmente',
    pt: 'Pagar anualmente',
    fr: 'Paiement annuel',
    it: 'Paga annualmente',
    ru: 'Оплачивать ежегодно',
  },
  'trialPaywall.period.savings': {
    en: 'save up to {percent}%',
    es: 'ahorra hasta un {percent} %',
    pt: 'economize até {percent}%',
    fr: 'économisez jusqu’à {percent} %',
    it: 'risparmia fino al {percent}%',
    ru: 'экономия до {percent}%',
  },
  'trialPaywall.card.customMinutes': {
    en: 'Custom minutes',
    es: 'Minutos personalizados',
    pt: 'Minutos personalizados',
    fr: 'Minutes personnalisées',
    it: 'Minuti personalizzati',
    ru: 'Индивидуальное количество минут',
  },
  'trialPaywall.card.customPricing': {
    en: 'Custom pricing',
    es: 'Precio personalizado',
    pt: 'Preço personalizado',
    fr: 'Tarification personnalisée',
    it: 'Prezzo personalizzato',
    ru: 'Индивидуальная стоимость',
  },
  'trialPaywall.card.total': {
    en: 'total',
    es: 'en total',
    pt: 'no total',
    fr: 'au total',
    it: 'in totale',
    ru: 'за год',
  },
  'trialPaywall.card.perMinute': {
    en: 'per minute',
    es: 'por minuto',
    pt: 'por minuto',
    fr: 'par minute',
    it: 'al minuto',
    ru: 'за минуту',
  },
  'trialPaywall.card.perMonth': {
    en: '/mo',
    es: '/mes',
    pt: '/mês',
    fr: '/mois',
    it: '/mese',
    ru: '/мес.',
  },
  'trialPaywall.card.minutesOne': {
    en: '{count} minute',
    es: '{count} minuto',
    pt: '{count} minuto',
    fr: '{count} minute',
    it: '{count} minuto',
    ru: '{count} минута',
  },
  'trialPaywall.card.minutesFew': {
    en: '{count} minutes',
    es: '{count} minutos',
    pt: '{count} minutos',
    fr: '{count} minutes',
    it: '{count} minuti',
    ru: '{count} минуты',
  },
  'trialPaywall.card.minutesMany': {
    en: '{count} minutes',
    es: '{count} minutos',
    pt: '{count} minutos',
    fr: '{count} minutes',
    it: '{count} minuti',
    ru: '{count} минут',
  },
  'trialPaywall.card.minutesOther': {
    en: '{count} minutes',
    es: '{count} minutos',
    pt: '{count} minutos',
    fr: '{count} minutes',
    it: '{count} minuti',
    ru: '{count} минуты',
  },
  'trialPaywall.benefits.yearlyMinutesOne': {
    en: 'Start from {count} minute, get more anytime',
    es: 'Empieza con {count} minuto y consigue más cuando quieras',
    pt: 'Comece com {count} minuto e adquira mais quando quiser',
    fr: 'Commencez avec {count} minute et obtenez-en plus à tout moment',
    it: 'Inizia con {count} minuto e acquistane altri quando vuoi',
    ru: 'Начните с {count} минуты и пополняйте баланс в любое время',
  },
  'trialPaywall.benefits.yearlyMinutesFew': {
    en: 'Start from {count} minutes, get more anytime',
    es: 'Empieza con {count} minutos y consigue más cuando quieras',
    pt: 'Comece com {count} minutos e adquira mais quando quiser',
    fr: 'Commencez avec {count} minutes et obtenez-en plus à tout moment',
    it: 'Inizia con {count} minuti e acquistane altri quando vuoi',
    ru: 'Начните с {count} минут и пополняйте баланс в любое время',
  },
  'trialPaywall.benefits.yearlyMinutesMany': {
    en: 'Start from {count} minutes, get more anytime',
    es: 'Empieza con {count} minutos y consigue más cuando quieras',
    pt: 'Comece com {count} minutos e adquira mais quando quiser',
    fr: 'Commencez avec {count} minutes et obtenez-en plus à tout moment',
    it: 'Inizia con {count} minuti e acquistane altri quando vuoi',
    ru: 'Начните с {count} минут и пополняйте баланс в любое время',
  },
  'trialPaywall.benefits.yearlyMinutesOther': {
    en: 'Start from {count} minutes, get more anytime',
    es: 'Empieza con {count} minutos y consigue más cuando quieras',
    pt: 'Comece com {count} minutos e adquira mais quando quiser',
    fr: 'Commencez avec {count} minutes et obtenez-en plus à tout moment',
    it: 'Inizia con {count} minuti e acquistane altri quando vuoi',
    ru: 'Начните с {count} минуты и пополняйте баланс в любое время',
  },
  'trialPaywall.benefits.monthlyMinutesOne': {
    en: '{count} minute a month',
    es: '{count} minuto al mes',
    pt: '{count} minuto por mês',
    fr: '{count} minute par mois',
    it: '{count} minuto al mese',
    ru: '{count} минута в месяц',
  },
  'trialPaywall.benefits.monthlyMinutesFew': {
    en: '{count} minutes a month',
    es: '{count} minutos al mes',
    pt: '{count} minutos por mês',
    fr: '{count} minutes par mois',
    it: '{count} minuti al mese',
    ru: '{count} минуты в месяц',
  },
  'trialPaywall.benefits.monthlyMinutesMany': {
    en: '{count} minutes a month',
    es: '{count} minutos al mes',
    pt: '{count} minutos por mês',
    fr: '{count} minutes par mois',
    it: '{count} minuti al mese',
    ru: '{count} минут в месяц',
  },
  'trialPaywall.benefits.monthlyMinutesOther': {
    en: '{count} minutes a month',
    es: '{count} minutos al mes',
    pt: '{count} minutos por mês',
    fr: '{count} minutes par mois',
    it: '{count} minuti al mese',
    ru: '{count} минуты в месяц',
  },
  'trialPaywall.cta.processing': {
    en: 'Processing...',
    es: 'Procesando...',
    pt: 'Processando...',
    fr: 'Traitement en cours…',
    it: 'Elaborazione...',
    ru: 'Обработка...',
  },
  'trialPaywall.cta.bookDemo': {
    en: 'Book a Demo',
    es: 'Reservar demo',
    pt: 'Agendar demonstração',
    fr: 'Réserver une démo',
    it: 'Prenota una demo',
    ru: 'Заказать демо',
  },
  'trialPaywall.cta.continueWithPlan': {
    en: 'Continue with {plan}',
    es: 'Continuar con {plan}',
    pt: 'Continuar com {plan}',
    fr: 'Continuer avec {plan}',
    it: 'Continua con {plan}',
    ru: 'Продолжить с тарифом {plan}',
  },
  'trialPaywall.error.generic': {
    en: 'Something went wrong. Please try again later.',
    es: 'Algo salió mal. Inténtalo de nuevo más tarde.',
    pt: 'Algo deu errado. Tente novamente mais tarde.',
    fr: 'Une erreur est survenue. Veuillez réessayer plus tard.',
    it: 'Qualcosa è andato storto. Riprova più tardi.',
    ru: 'Что-то пошло не так. Попробуйте ещё раз позже.',
  },
  'trialPaywall.chip.business': {
    en: 'Most precise results',
    es: 'Resultados más precisos',
    pt: 'Resultados mais precisos',
    fr: 'Résultats les plus précis',
    it: 'Risultati più precisi',
    ru: 'Самые точные результаты',
  },
  'trialPaywall.chip.creator': {
    en: 'Best for getting started',
    es: 'La mejor opción para empezar',
    pt: 'Melhor para começar',
    fr: 'Idéal pour commencer',
    it: 'Ideale per iniziare',
    ru: 'Лучший вариант для старта',
  },
  'trialPaywall.chip.creatorPro': {
    en: 'Most popular',
    es: 'Más popular',
    pt: 'Mais popular',
    fr: 'Le plus populaire',
    it: 'Più popolare',
    ru: 'Самый популярный',
  },
  'trialPaywall.legacyChip.business': {
    en: 'Most precise result',
    es: 'El resultado más preciso',
    pt: 'Resultado mais preciso',
    fr: 'Résultat le plus précis',
    it: 'Risultato più preciso',
    ru: 'Самый точный результат',
  },
  'trialPaywall.legacyChip.creator': {
    en: 'Great for getting started',
    es: 'Ideal para empezar',
    pt: 'Ótimo para começar',
    fr: 'Idéal pour débuter',
    it: 'Ottimo per iniziare',
    ru: 'Отличный вариант для старта',
  },
  'trialPaywall.include.aiDubbing': {
    en: 'AI dubbing in 135+ languages',
    es: 'Doblaje con IA en más de 135 idiomas',
    pt: 'Dublagem com IA em mais de 135 idiomas',
    fr: 'Doublage par IA dans plus de 135 langues',
    it: 'Doppiaggio AI in oltre 135 lingue',
    ru: 'ИИ-озвучка на более чем 135 языках',
  },
  'trialPaywall.include.voiceCloning': {
    en: 'Voice cloning in 32 languages',
    es: 'Clonación de voz en 32 idiomas',
    pt: 'Clonagem de voz em 32 idiomas',
    fr: 'Clonage vocal dans 32 langues',
    it: 'Clonazione vocale in 32 lingue',
    ru: 'Клонирование голоса на 32 языках',
  },
  'trialPaywall.include.transcriptionTranslation': {
    en: 'Automatic transcription & translation',
    es: 'Transcripción y traducción automáticas',
    pt: 'Transcrição e tradução automáticas',
    fr: 'Transcription et traduction automatiques',
    it: 'Trascrizione e traduzione automatiche',
    ru: 'Автоматическая транскрипция и перевод',
  },
  'trialPaywall.include.editor': {
    en: 'Built-in video editor',
    es: 'Editor de video integrado',
    pt: 'Editor de vídeo integrado',
    fr: 'Éditeur vidéo intégré',
    it: 'Editor video integrato',
    ru: 'Встроенный видеоредактор',
  },
  'trialPaywall.benefit.business.marketsTitle': {
    en: 'Many markets at once',
    es: 'Varios mercados a la vez',
    pt: 'Vários mercados de uma só vez',
    fr: 'Plusieurs marchés à la fois',
    it: 'Più mercati contemporaneamente',
    ru: 'Несколько рынков одновременно',
  },
  'trialPaywall.benefit.business.marketsDescription': {
    en: 'translate into multiple languages in one flow',
    es: 'traduce a varios idiomas en un solo flujo',
    pt: 'traduza para vários idiomas em um único fluxo',
    fr: 'traduisez vers plusieurs langues en un seul processus',
    it: 'traduci in più lingue con un unico flusso',
    ru: 'переводите на несколько языков в рамках одного процесса',
  },
  'trialPaywall.benefit.business.terminologyTitle': {
    en: 'Consistent terminology',
    es: 'Terminología coherente',
    pt: 'Terminologia consistente',
    fr: 'Terminologie cohérente',
    it: 'Terminologia coerente',
    ru: 'Единая терминология',
  },
  'trialPaywall.benefit.business.terminologyDescription': {
    en: 'keep brand and product terms aligned',
    es: 'mantén coherentes los términos de marca y producto',
    pt: 'mantenha os termos da marca e do produto alinhados',
    fr: 'harmonisez les termes de la marque et des produits',
    it: 'mantieni coerenti i termini del brand e dei prodotti',
    ru: 'сохраняйте единообразие терминов бренда и продукта',
  },
  'trialPaywall.benefit.business.teamTitle': {
    en: 'Faster team output',
    es: 'Mayor productividad del equipo',
    pt: 'Mais produtividade para a equipe',
    fr: 'Production d’équipe accélérée',
    it: 'Maggiore produttività del team',
    ru: 'Более быстрая работа команды',
  },
  'trialPaywall.benefit.business.teamDescription': {
    en: 'use 10 voices, collaborate with 5 team members and get priority processing',
    es: 'usa 10 voces, 5 miembros y procesamiento prioritario',
    pt: 'use 10 vozes, 5 membros e processamento prioritário',
    fr: 'utilisez 10 voix, 5 membres et le traitement prioritaire',
    it: 'usa 10 voci, 5 membri e l’elaborazione prioritaria',
    ru: 'используйте 10 голосов, работайте вместе с 5 участниками команды и получайте приоритетную обработку',
  },
  'trialPaywall.benefit.business.minutesDescription': {
    en: 'built for teams localizing content regularly',
    es: 'creado para equipos que adaptan contenido con regularidad',
    pt: 'criado para equipes que adaptam conteúdo regularmente',
    fr: 'conçu pour les équipes qui adaptent régulièrement du contenu',
    it: 'pensato per i team che localizzano contenuti regolarmente',
    ru: 'для команд, которые регулярно локализуют контент',
  },
  'trialPaywall.benefit.creator.projectsTitle': {
    en: 'Try real projects',
    es: 'Prueba proyectos reales',
    pt: 'Experimente projetos reais',
    fr: 'Testez sur de vrais projets',
    it: 'Prova progetti reali',
    ru: 'Попробуйте на реальных проектах',
  },
  'trialPaywall.benefit.creator.projectsDescription': {
    en: 'localize, edit and export your own videos',
    es: 'adapta, edita y exporta tus propios videos',
    pt: 'adapte, edite e exporte seus próprios vídeos',
    fr: 'adaptez, modifiez et exportez vos propres vidéos',
    it: 'localizza, modifica ed esporta i tuoi video',
    ru: 'локализуйте, редактируйте и экспортируйте свои видео',
  },
  'trialPaywall.benefit.creator.voiceTitle': {
    en: 'Customize voice cloning',
    es: 'Personaliza la clonación de voz',
    pt: 'Personalize a clonagem de voz',
    fr: 'Personnalisez le clonage vocal',
    it: 'Personalizza la clonazione vocale',
    ru: 'Настройте клонирование голоса',
  },
  'trialPaywall.benefit.creator.voiceDescription': {
    en: 'tune cloned voices and create your own library',
    es: 'ajusta las voces clonadas y crea tu propia biblioteca',
    pt: 'ajuste vozes clonadas e crie sua própria biblioteca',
    fr: 'ajustez les voix clonées et créez votre propre bibliothèque',
    it: 'regola le voci clonate e crea la tua libreria',
    ru: 'настраивайте клонированные голоса и создавайте свою библиотеку',
  },
  'trialPaywall.benefit.creator.workflowTitle': {
    en: 'Simple solo workflow',
    es: 'Flujo individual sencillo',
    pt: 'Fluxo individual simples',
    fr: 'Processus individuel simple',
    it: 'Flusso individuale semplice',
    ru: 'Простой процесс для самостоятельной работы',
  },
  'trialPaywall.benefit.creator.workflowDescription': {
    en: 'best for occasional, self-serve localization',
    es: 'ideal para adaptaciones ocasionales y autoservicio',
    pt: 'ideal para adaptação ocasional feita por conta própria',
    fr: 'idéal pour une adaptation autonome et occasionnelle',
    it: 'ideale per localizzazioni occasionali e autonome',
    ru: 'лучший вариант для самостоятельной нерегулярной локализации',
  },
  'trialPaywall.benefit.creator.minutesDescription': {
    en: 'for simple one-time projects',
    es: 'para proyectos puntuales sencillos',
    pt: 'para projetos simples e pontuais',
    fr: 'pour les projets ponctuels simples',
    it: 'per semplici progetti una tantum',
    ru: 'для простых разовых проектов',
  },
  'trialPaywall.benefit.creatorPro.lipSyncTitle': {
    en: 'Lip-sync your translated videos',
    es: 'Aplica Lip-sync a tus videos traducidos',
    pt: 'Aplique Lip-sync aos vídeos traduzidos',
    fr: 'Ajoutez le Lip-sync à vos vidéos traduites',
    it: 'Applica il lip-sync ai video tradotti',
    ru: 'Добавляйте липсинк к переведённым видео',
  },
  'trialPaywall.benefit.creatorPro.lipSyncDescription': {
    en: 'sync mouth movement with translated speech',
    es: 'sincroniza el movimiento de la boca con el habla traducida',
    pt: 'sincronize o movimento da boca com a fala traduzida',
    fr: 'alignez les mouvements de la bouche sur les paroles traduites',
    it: 'sincronizza il movimento della bocca con il parlato tradotto',
    ru: 'синхронизируйте движения губ с переведённой речью',
  },
  'trialPaywall.benefit.creatorPro.editsTitle': {
    en: 'Ready-to-publish edits',
    es: 'Edición lista para publicar',
    pt: 'Edições prontas para publicar',
    fr: 'Modifications prêtes à publier',
    it: 'Modifiche pronte per la pubblicazione',
    ru: 'Подготовка к публикации',
  },
  'trialPaywall.benefit.creatorPro.editsDescription': {
    en: 'work with scripts, subtitles and voice settings',
    es: 'trabaja con guiones, subtítulos y ajustes de voz',
    pt: 'trabalhe com roteiros, legendas e configurações de voz',
    fr: 'travaillez sur les scripts, les sous-titres et les réglages vocaux',
    it: 'lavora con copioni, sottotitoli e impostazioni vocali',
    ru: 'работайте со сценариями, субтитрами и настройками голоса',
  },
  'trialPaywall.benefit.creatorPro.teamTitle': {
    en: 'Work together with your team',
    es: 'Trabaja con tu equipo',
    pt: 'Trabalhe com sua equipe',
    fr: 'Travaillez avec votre équipe',
    it: 'Lavora con il tuo team',
    ru: 'Работайте вместе с командой',
  },
  'trialPaywall.benefit.creatorPro.teamDescription': {
    en: 'share your subscription with 3 team members',
    es: 'comparte tu suscripción con 3 miembros del equipo',
    pt: 'compartilhe sua assinatura com 3 membros da equipe',
    fr: 'partagez votre abonnement avec 3 membres de l’équipe',
    it: 'condividi l’abbonamento con 3 membri del team',
    ru: 'делитесь подпиской с 3 участниками команды',
  },
  'trialPaywall.benefit.creatorPro.minutesDescription': {
    en: 'translate more videos, with extra minutes available',
    es: 'traduce más videos, con minutos adicionales disponibles',
    pt: 'traduza mais vídeos com minutos adicionais disponíveis',
    fr: 'traduisez plus de vidéos avec des minutes supplémentaires disponibles',
    it: 'traduci più video con minuti aggiuntivi disponibili',
    ru: 'переводите больше видео — при необходимости доступны дополнительные минуты',
  },
  'trialPaywall.benefit.enterprise.minutesTitle': {
    en: 'Custom number of minutes',
    es: 'Cantidad de minutos personalizada',
    pt: 'Quantidade personalizada de minutos',
    fr: 'Nombre de minutes personnalisé',
    it: 'Quantità di minuti personalizzata',
    ru: 'Индивидуальное количество минут',
  },
  'trialPaywall.benefit.enterprise.minutesDescription': {
    en: 'for high-volume localization operations',
    es: 'para operaciones de adaptación de gran volumen',
    pt: 'para operações de adaptação de alto volume',
    fr: 'pour les opérations d’adaptation à grande échelle',
    it: 'per operazioni di localizzazione ad alto volume',
    ru: 'для масштабной локализации',
  },
  'trialPaywall.benefit.enterprise.reviewTitle': {
    en: 'Managed review',
    es: 'Revisión gestionada',
    pt: 'Revisão gerenciada',
    fr: 'Service de révision',
    it: 'Revisione gestita',
    ru: 'Управляемая проверка',
  },
  'trialPaywall.benefit.enterprise.reviewDescription': {
    en: 'add human review for high-stakes output',
    es: 'añade revisión humana para contenidos críticos',
    pt: 'adicione revisão humana para conteúdos críticos',
    fr: 'ajoutez une révision humaine pour les contenus sensibles',
    it: 'aggiungi la revisione umana per i contenuti critici',
    ru: 'добавьте проверку специалистом для важных материалов',
  },
  'trialPaywall.benefit.enterprise.procurementTitle': {
    en: 'Procurement-ready',
    es: 'Listo para compras corporativas',
    pt: 'Pronto para compras corporativas',
    fr: 'Prêt pour les achats d’entreprise',
    it: 'Pronto per gli acquisti aziendali',
    ru: 'Готово для корпоративных закупок',
  },
  'trialPaywall.benefit.enterprise.procurementDescription': {
    en: 'use custom contracts and invoicing',
    es: 'usa contratos y facturación personalizados',
    pt: 'use contratos e faturamento personalizados',
    fr: 'utilisez des contrats et une facturation personnalisés',
    it: 'usa contratti e fatturazione personalizzati',
    ru: 'используйте индивидуальные договоры и выставление счетов',
  },
  'trialPaywall.benefit.enterprise.supportTitle': {
    en: 'Dedicated support',
    es: 'Soporte dedicado',
    pt: 'Suporte dedicado',
    fr: 'Assistance dédiée',
    it: 'Assistenza dedicata',
    ru: 'Персональная поддержка',
  },
  'trialPaywall.benefit.enterprise.supportDescription': {
    en: 'get dedicated customer success support for complex workflows',
    es: 'recibe asistencia especializada para flujos complejos',
    pt: 'receba suporte especializado para fluxos complexos',
    fr: 'bénéficiez d’un accompagnement dédié pour les processus complexes',
    it: 'ricevi assistenza dedicata per i flussi complessi',
    ru: 'получите помощь специалиста для сложных процессов',
  },
  'trialPaywall.upgradeBanner.title': {
    en: 'Upgrade to translate the full video',
    es: 'Mejora el plan para traducir el video completo',
    pt: 'Faça upgrade para traduzir o vídeo completo',
    fr: 'Passez à un forfait supérieur pour traduire toute la vidéo',
    it: 'Passa a un piano superiore per tradurre l’intero video',
    ru: 'Выберите тариф, чтобы перевести видео целиком',
  },
  'trialPaywall.upgradeBanner.description': {
    en: 'To translate videos over a minute long without a watermark, choose a plan that suits your needs.',
    es: 'Para traducir videos de más de un minuto sin marca de agua, elige el plan que mejor se adapte a ti.',
    pt: 'Para traduzir vídeos com mais de um minuto sem marca-d’água, escolha o plano ideal para você.',
    fr: 'Pour traduire sans filigrane des vidéos de plus d’une minute, choisissez le forfait qui vous convient.',
    it: 'Per tradurre senza filigrana video più lunghi di un minuto, scegli il piano più adatto alle tue esigenze.',
    ru: 'Чтобы переводить видео длиннее минуты без водяного знака, выберите подходящий тариф.',
  },
  'trialPaywall.upgradeBanner.cta': {
    en: 'Upgrade',
    es: 'Mejorar el plan',
    pt: 'Fazer upgrade',
    fr: 'Passer à un forfait supérieur',
    it: 'Passa a un piano superiore',
    ru: 'Выбрать тариф',
  },
  'trialPaywall.header.moreMinutesShort': {
    en: 'More min',
    es: 'Más min',
    pt: 'Mais min',
    fr: 'Plus de min',
    it: 'Più min',
    ru: 'Ещё мин.',
  },
  'trialPaywall.header.moreMinutes': {
    en: 'More minutes',
    es: 'Más minutos',
    pt: 'Mais minutos',
    fr: 'Plus de minutes',
    it: 'Più minuti',
    ru: 'Больше минут',
  },
  'trialPaywall.header.viewPlans': {
    en: 'View plans',
    es: 'Ver planes',
    pt: 'Ver planos',
    fr: 'Voir les offres',
    it: 'Vedi i piani',
    ru: 'Посмотреть тарифы',
  },
} satisfies Record<string, TranslationRow>;

export type TrialPaywallTranslationKey = keyof typeof TRIAL_PAYWALL_TRANSLATION_ROWS;
type TrialPaywallDictionaries = Record<CatalogLocale, Record<TrialPaywallTranslationKey, string>>;

const dictionaries = Object.fromEntries(
  CATALOG_LOCALES.map((locale) => [
    locale,
    Object.fromEntries(
      Object.entries(TRIAL_PAYWALL_TRANSLATION_ROWS).map(([key, translations]) => [key, translations[locale]]),
    ),
  ]),
) as TrialPaywallDictionaries;

export const TRIAL_PAYWALL_TRANSLATIONS = defineDomainTranslations(dictionaries);
